import { brokersService } from '@/services';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface AdvisoryFilterOption {
  label: string;
  value: string;
}

/**
 * Opções do filtro de Assessoria da tela de Corretores.
 *
 * A fonte é `/parties/brokers/advisories`, que devolve só as assessorias
 * vinculadas a alguma corretora. O cadastro completo (`/parties/agencies`)
 * traz também assessorias sem nenhuma corretora — como opção de filtro elas
 * sempre resultariam em lista vazia.
 *
 * Dois valores diferentes da mesma assessoria são usados aqui:
 * - `partyCode` é o que vai na query (`advisoryId`), porque o backend resolve o
 *   código em entityId antes de filtrar (ListBrokersHandler.findPartyByCode).
 * - `entityId` é o que a corretora carrega em `advisoryId`, e por isso é a chave
 *   do mapa que traduz o vínculo em nome na coluna Assessoria.
 */
export function useAdvisoryFilterOptions(enabled: boolean) {
  const { data, isLoading } = useQuery({
    queryKey: ['broker-advisories'],
    queryFn: async () => {
      const response = await brokersService.listBrokerAdvisories();

      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Erro ao carregar assessorias');
      }

      return response.data;
    },
    enabled,
    // A varredura de parties no backend é cara e o vínculo corretora/assessoria
    // praticamente não muda dentro de uma sessão.
    staleTime: 1000 * 60 * 30,
  });

  const items = useMemo<AdvisoryFilterOption[]>(() => {
    // Já vem ordenado por nome do backend.
    return (data ?? [])
      .filter(advisory => advisory.partyCode)
      .map(advisory => ({
        label: advisory.name || advisory.partyCode,
        value: advisory.partyCode,
      }));
  }, [data]);

  const nameByEntityId = useMemo(() => {
    const map = new Map<string, string>();
    (data ?? []).forEach(advisory => {
      if (advisory.entityId && advisory.name) {
        map.set(advisory.entityId, advisory.name);
      }
    });
    return map;
  }, [data]);

  return { items, nameByEntityId, isLoading };
}
